/* @flow */

/**
 * WARNING: because of how routes are registered in `useRegistrationRoute`,
 * this file should only export `RouteData` types.
 *
 * The not found route MUST be the last one defined.
 */

import { either } from 'ramda';

import {
  standardAuthenticatedPageRedirect,
  standardUnauthenticatedPageRedirect,
  redirectToHomeWhenSignupDisabled,
  redirectToHomeWhenSignupCompleted,
  redirectToHomeWhenAuthenticated,
  redirectWhenRedirectAndAuthenticated,
  redirectToSignupUnlessCompleted,
  redirectToLoginIfOnboardingCompletedOnDevice,
  redirectToLoginURL,
} from 'src/shared/app/base/route/util/redirectUtils';
import {
  getBraindateSlug,
  isSplashScreenShown,
  isPermissionScreenShown,
  isOnboardingScreenShown,
  isMarketShown,
} from 'src/shared/app/base/route/util/routeUtils';
import BlankTemplate from 'src/shared/app/base/component/template/BlankTemplate';
import BlankWithDesktopSideBarTemplate from 'src/shared/app/base/component/template/BlankWithDesktopSideBarTemplate';
import WithNavTemplate from 'src/shared/app/base/component/template/WithNavTemplate';
import WithDesktopNavTemplate from 'src/shared/app/base/component/template/WithDesktopNavTemplate';
import LazyLightMain from 'plugin/light/component/LazyLightMain';
import { isVirtualBraindateOverlayShown } from 'plugin/virtual-braindate/selector/virtualBraindateSelectors';
import LazyBlankMain from 'src/shared/app/blank/LazyBlankMain';
import LazyAccountMain from 'src/shared/app/account/base/component/LazyAccountMain';
import LazyProfileEditMain from 'src/shared/app/account/profile/component/edit/LazyProfileEditMain';
import LazyLoginMain from 'src/shared/app/authentication/component/login/LazyLoginMain';
import LazyResetPasswordMain from 'src/shared/app/authentication/component/reset-password/LazyResetPasswordMain';
import LazyLoginSupportMain from 'src/shared/app/authentication/component/support/login/LazyLoginSupportMain';
import LazyPasswordSupportMain from 'src/shared/app/authentication/component/support/password/LazyPasswordSupportMain';
import LazyRetrieveUsernameMain from 'src/shared/app/authentication/component/support/login/username/LazyRetrieveUsernameMain';
import LazyLoginLinkMain from 'src/shared/app/authentication/component/support/login/link/LazyLoginLinkMain';
import LazySupportContactMain from 'src/shared/app/authentication/component/support/contact/LazySupportContactMain';
import LazyBraindateMain from 'src/shared/app/braindate/component/LazyBraindateMain';
import LazyBraindatesMain from 'src/shared/app/braindate/component/main/LazyBraindatesMain';
import LazyConversationMain from 'src/shared/app/conversation/component/LazyConversationMain';
import LazyClientErrorMain from 'src/shared/app/error/component/LazyClientErrorMain';
import LazyServerErrorMain from 'src/shared/app/error/component/LazyServerErrorMain';
import LazyFAQMain from 'src/shared/app/faq/component/LazyFAQMain';
import LazyMarketMain from 'src/shared/app/market/component/LazyMarketMain';
import LazyNotFoundMain from 'src/shared/app/not-found/component/LazyNotFoundMain';
import LazyOnboardingMain from 'src/shared/app/onboarding/component/LazyOnboardingMain';
import LazySplashScreen from 'src/shared/app/onboarding/component/splash/LazySplashScreen';
import LazyPermissionMain from 'src/shared/app/permission/component/LazyPermissionMain';
import LazyPreferencesMain from 'src/shared/app/preference/component/LazyPreferencesMain';
import LazySignupMain from 'src/shared/app/signup/component/LazySignupMain';
import LazySupportMain from 'src/shared/app/support/component/LazySupportMain';
import LazyNewTopicMain from 'src/shared/app/topic/new/component/LazyNewTopicMain';
import LazyUIMain from 'src/shared/app/ui/component/LazyUIMain';
import LazyUserMain from 'src/shared/app/user/component/LazyUserMain';
import LazyInviteMain from 'src/shared/app/user/component/LazyInviteMain';
import LazyTopicDetailsMain from 'src/shared/components/domain/topic/details/LazyTopicDetailsMain';
import LazyTopicEditMain from 'src/shared/components/domain/topic/form/LazyTopicEditMain';
import LazyNewBraindateMain from 'src/shared/components/domain/topic/form/offer/LazyNewBraindateMain';
import LazyNewGroupBraindateMain from 'src/shared/components/domain/topic/form/group-braindate/LazyNewGroupBraindateMain';
import LazyOauthRedirect from 'src/shared/app/account/oauth/components/LazyOauthRedirect';

import type { State } from 'src/shared/core/type/reduxTypes';

export const homeRoute = {
  path: '/',
  template: (state: State, { areEmailNotificationsEnabled }: Object) => {
    if (isSplashScreenShown(state)) {
      return BlankTemplate;
    }

    if (isOnboardingScreenShown(state)) {
      return BlankWithDesktopSideBarTemplate;
    }

    if (isPermissionScreenShown(state, areEmailNotificationsEnabled)) {
      return BlankWithDesktopSideBarTemplate;
    }

    return WithNavTemplate;
  },
  main: (state: State, { areEmailNotificationsEnabled }: Object) => {
    if (isSplashScreenShown(state)) {
      return LazySplashScreen;
    }

    if (isOnboardingScreenShown(state)) {
      return LazyOnboardingMain;
    }

    if (isPermissionScreenShown(state, areEmailNotificationsEnabled)) {
      return LazyPermissionMain;
    }

    return LazyMarketMain;
  },
  help: isMarketShown,
  redirect: either(
    redirectWhenRedirectAndAuthenticated,
    either(
      redirectToSignupUnlessCompleted,
      redirectToLoginIfOnboardingCompletedOnDevice,
    ),
  ),
};

export const signupRoute = {
  path: '/signup/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazySignupMain,
  redirect: either(
    redirectToHomeWhenSignupDisabled,
    redirectToHomeWhenSignupCompleted,
  ),
  scrollToTop: true,
};

export const loginRoute = {
  path: '/login/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginMain,
  redirect: either(redirectToHomeWhenAuthenticated, redirectToLoginURL),
  scrollToTop: true,
};

export const loginSupportRoute = {
  path: '/login-support/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginSupportMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const retrieveUsernameRoute = {
  path: '/login-support/username/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyRetrieveUsernameMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const loginLinkRoute = {
  path: '/login-support/link/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginLinkMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const contactRoute = {
  path: '/support-contact/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazySupportContactMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const passwordSupportRoute = {
  path: '/password-support/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyPasswordSupportMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const resetPasswordRoute = {
  path: '/reset-password/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyResetPasswordMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicRoute = {
  path: '/topics/:id/',
  template: WithDesktopNavTemplate,
  main: LazyTopicDetailsMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicEditRoute = {
  path: '/topics/:id/edit/',
  template: WithDesktopNavTemplate,
  main: LazyTopicEditMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicInviteRoute = {
  path: '/topics/:id/invite/:userId',
  template: WithDesktopNavTemplate,
  main: LazyTopicDetailsMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const newTopicRoute = {
  path: '/new-topic/',
  template: WithNavTemplate,
  main: LazyNewTopicMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const newBraindateRoute = {
  path: (state: State) => `/new-${getBraindateSlug(state)}/`,
  template: WithNavTemplate,
  main: LazyNewBraindateMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const newGroupBraindateRoute = {
  path: (state: State) => `/new-group-${getBraindateSlug(state)}/`,
  template: WithNavTemplate,
  main: LazyNewGroupBraindateMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const braindatesRoute = {
  path: (state: State) => `/${getBraindateSlug(state)}/`,
  template: WithNavTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const invitationsRoute = {
  path: (state: State) => `/${getBraindateSlug(state)}/invitations/`,
  template: WithNavTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const confirmedBraindatesRoute = {
  path: (state: State) => `/${getBraindateSlug(state)}/confirmed/`,
  template: WithNavTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const braindateRoute = {
  path: (state: State) => `/${getBraindateSlug(state)}/:id/`,
  template: WithDesktopNavTemplate,
  main: LazyBraindateMain,
  help: (state: State) => !isVirtualBraindateOverlayShown(state),
  redirect: standardAuthenticatedPageRedirect,
};

export const conversationRoute = {
  path: '/topics/:id/conversation/',
  template: WithDesktopNavTemplate,
  main: LazyConversationMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const preferencesRoute = {
  path: '/preferences/',
  template: WithNavTemplate,
  main: LazyPreferencesMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const userRoute = {
  path: '/users/:id/',
  template: WithDesktopNavTemplate,
  main: LazyUserMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const userInviteRoute = {
  path: '/users/:id/invite',
  template: WithNavTemplate,
  main: LazyInviteMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const profileRoute = {
  path: '/account/profile/',
  template: WithNavTemplate,
  main: LazyAccountMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const ownTopicsRoute = {
  path: '/account/topics/',
  template: WithNavTemplate,
  main: LazyAccountMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const availabilityRoute = {
  path: '/account/availability/',
  template: WithNavTemplate,
  main: LazyAccountMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const profileEditRoute = {
  path: '/account/profile/edit/',
  template: WithDesktopNavTemplate,
  main: LazyProfileEditMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const faqRoute = {
  path: '/faq/',
  template: WithNavTemplate,
  main: LazyFAQMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const supportRoute = {
  path: '/support/',
  template: WithNavTemplate,
  main: LazySupportMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const oauthRedirect = {
  path: '/oauth2/redirect/',
  template: BlankTemplate,
  main: LazyOauthRedirect,
};

export const uiRoute = {
  path: '/ui/',
  template: BlankTemplate,
  main: LazyUIMain,
  scrollToTop: true,
};

export const serverErrorRoute = {
  path: '/server-error/',
  template: BlankTemplate,
  main: LazyServerErrorMain,
};

export const clientErrorRoute = {
  path: '/client-error/',
  template: BlankTemplate,
  main: LazyClientErrorMain,
};

export const lightRoute = {
  path: '/light/',
  template: BlankTemplate,
  main: LazyLightMain,
};

export const blankRoute = {
  path: '/blank/',
  template: BlankTemplate,
  main: LazyBlankMain,
};

export const blankWithDesktopSidebarRoute = {
  path: '/blank-with-desktop-sidebar/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyBlankMain,
};

export const withNavRoute = {
  path: '/with-nav/',
  template: WithNavTemplate,
  main: LazyBlankMain,
};

export const withDesktopNavRoute = {
  path: '/with-desktop-nav/',
  template: WithDesktopNavTemplate,
  main: LazyBlankMain,
};

// DON'T DEFINED NEW ROUTES UNDER THIS LINE

export const notFoundRoute = {
  path: '*',
  template: WithNavTemplate,
  main: LazyNotFoundMain,
  scrollToTop: true,
};
